import React, { useMemo } from 'react';
import { convertVideoCoordinates } from '../../utils/annotationUtils';
import { getFrameForTime } from '../../utils/frameIndex';
import { VIDEO_WIDTH, VIDEO_HEIGHT } from '../../utils/constants';

const TrajectoryOverlay = ({
  trajectories = [],
  currentTime = 0,
  zoom = 1,
  pan = { x: 0, y: 0 },
  videoRef,
  displayDimensions = { width: VIDEO_WIDTH, height: VIDEO_HEIGHT },
}) => {
  const currentFrame = getFrameForTime(currentTime);

  const nativeWidth = videoRef?.current?.width || VIDEO_WIDTH;
  const nativeHeight = videoRef?.current?.height || VIDEO_HEIGHT;

  const applyZoomPan = (pt) => ({
    x: pt.x * zoom - pan.x,
    y: pt.y * zoom - pan.y,
  });

  const convertPoint = (x, y) => {
    const display = convertVideoCoordinates(
      x, y,
      nativeWidth, nativeHeight,
      displayDimensions.width, displayDimensions.height
    );
    return applyZoomPan(display);
  };

  const LINGER_FRAMES = 20;

  const visibleTrajectories = useMemo(() => {
    const result = [];
    for (const t of trajectories) {
      if (currentFrame >= t.startFrame && currentFrame <= t.endFrame) {
        result.push({ traj: t, lingerOpacity: null });
      } else if (currentFrame > t.endFrame && currentFrame <= t.endFrame + LINGER_FRAMES) {
        const offset = currentFrame - t.endFrame;
        result.push({ traj: t, lingerOpacity: 1 - offset / LINGER_FRAMES });
      }
    }
    return result;
  }, [trajectories, currentFrame]);

  if (visibleTrajectories.length === 0) return null;

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: displayDimensions.width,
        height: displayDimensions.height,
        pointerEvents: 'none',
        zIndex: 2,
        overflow: 'visible',
      }}
    >
      {visibleTrajectories.map(({ traj, lingerOpacity }, i) => {
        const bouncePoint = traj.bounceFrame != null
          ? traj.points.find(p => p.frame === traj.bounceFrame)
          : null;
        const bounce = bouncePoint ? convertPoint(bouncePoint.x, bouncePoint.y) : null;

        // During linger period: only show the fading bounce circle, not the path
        if (lingerOpacity !== null) {
          if (!bounce) return null;
          return (
            <circle
              key={i}
              cx={bounce.x}
              cy={bounce.y}
              r={6}
              fill={`rgba(0, 220, 255, ${(0.85 * lingerOpacity).toFixed(3)})`}
              stroke={`rgba(0, 220, 255, ${(0.35 * lingerOpacity).toFixed(3)})`}
              strokeWidth={3}
            />
          );
        }

        const futurePoints = traj.points.filter(p => p.frame >= currentFrame);
        if (futurePoints.length < 2) return null;

        const svgPoints = futurePoints
          .map(p => {
            const { x, y } = convertPoint(p.x, p.y);
            return `${x},${y}`;
          })
          .join(' ');

        return (
          <g key={i}>
            <polyline
              points={svgPoints}
              fill="none"
              stroke="rgba(255, 220, 50, 0.75)"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {bounce && (
              <circle
                cx={bounce.x}
                cy={bounce.y}
                r={6}
                fill="rgba(0, 220, 255, 0.85)"
                stroke="rgba(0, 220, 255, 0.35)"
                strokeWidth={3}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
};

export default TrajectoryOverlay;
